// src/screens/CartOverview.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import './CartOverview.css';
import { useCart } from '../../CartContext';

import backArrow from '../../assets/backarrow-icon.png';
import deleteIcon from '../../assets/delete-outline-icon.png';

import RemoveFromCartDrawer from '../../components/popups/RemoveFromCartDrawer';

function CartOverview({ user }) {
  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();
  const { removeFromCart, syncWithServer } = useCart();

  // Drawer state
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const res = await api.get(`/cart/${user.username}`);
        // Support both old ({ cart: { items: [...] } }) and new ({ items: [...] }) payload shapes
        const items = Array.isArray(res.data?.items)
          ? res.data.items
          : res.data?.cart?.items || [];

        const validItems = items.filter((item) => item.productId);
        setCartItems(validItems);

        // keep badge count in sync
        syncWithServer(validItems.map((i) => i.productId._id));
      } catch (err) {
        console.error('❌ Failed to load cart:', err);
      }
    };

    fetchCart();
  }, [user.username, syncWithServer]);

  // Open drawer
  const handleOpenRemoveDrawer = (productId) => {
    setSelectedProductId(productId);
    setShowDrawer(true);
  };

  // Confirm remove
  const handleConfirmRemove = async () => {
    try {
      await removeFromCart(selectedProductId);
      setCartItems((prev) =>
        prev.filter((item) => item.productId._id !== selectedProductId)
      );
    } catch (err) {
      console.error('❌ Remove from cart failed:', err);
    } finally {
      setShowDrawer(false);
      setSelectedProductId(null);
    }
  };

  // ---- helpers for pricing display ----
  const fmtPi = (n) =>
    typeof n === 'number' && Number.isFinite(n) ? `${n.toFixed(2)} Pi` : null;

  const getPriceParts = (product) => {
    const pv = product?.pricingView;
    if (pv && typeof pv.basePrice === 'number') {
      const base = pv.basePrice;
      const eff = pv.effectivePrice ?? pv.basePrice;
      const onSale = !!pv.isOnFlashSale && eff !== null && eff < base;
      const percent = pv.percentOff ?? null;
      return { base, eff, onSale, percent };
    }
    // Fallback (legacy): show stored price (raw) if pricingView missing
    if (product?.pricingType === 'raw' && typeof product?.price === 'number') {
      return { base: product.price, eff: product.price, onSale: false, percent: null };
    }
    // final fallback: nothing
    return { base: null, eff: null, onSale: false, percent: null };
  };

  return (
    <div className="cart-container">
      <div className="cart-header">
        <img
          src={backArrow}
          alt="Back"
          className="cart-back-icon"
          onClick={() => navigate(-1)}
        />
        <h2 className="cart-title">My Cart</h2>
      </div>

      <div className="cart-scroll-content">
        {cartItems.length === 0 ? (
          <p className="cart-empty">Your cart is empty.</p>
        ) : (
          <div className="cart-grid">
            {cartItems
              .filter(({ productId }) => productId && productId.media)
              .map(({ productId }) => {
                const { base, eff, onSale, percent } = getPriceParts(productId);

                return (
                  <div
                    key={productId._id}
                    className="cart-card"
                    onClick={() =>
                      navigate(`/product/${productId._id}`, {
                        state: { fromCart: true },
                      })
                    }
                  >
                    {/* SALE BADGE (top-left) */}
                    {onSale && (
                      <div className="cart-sale-badge">
                        -{percent ?? Math.round(((base - eff) / base) * 100)}%
                      </div>
                    )}

                    <img
                      src={productId.media?.[0]}
                      alt="product"
                      className="cart-img"
                    />

                    <div className="cart-info">
                      <p className="cart-name">{productId.name}</p>

                      {/* price block: strike-through base, show sale next to it */}

<div className="cart-price-row">
  <span className={onSale ? 'cart-price cart-price-sale' : 'cart-price'}>
    {fmtPi(onSale ? eff : (eff ?? base))}
  </span>
</div>


                    </div>

                    <img
                      src={deleteIcon}
                      alt="Remove"
                      className="cart-remove-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenRemoveDrawer(productId._id);
                      }}
                    />
                  </div>
                );
              })}
          </div>
        )}
      </div>

      <RemoveFromCartDrawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        onConfirm={handleConfirmRemove}
      />
    </div>
  );
}

export default CartOverview;
